import { Download, BookOpen, Video, Clock, Briefcase, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import AppHeader from './AppHeader';
import { toAppUrl } from '../constants/siteDomains';
import {
  TUTORIAL_GUIDES,
  TUTORIAL_FEATURED_VIDEO,
  CONSTELADOR_CLUB_WHATSAPP_URL,
  getTutorialDownloadUrl,
  getTutorialVideoEmbedUrl,
  getTutorialVideoWatchUrl,
  type TutorialAccent,
} from '../constants/tutorials';

const accentStyles: Record<
  TutorialAccent,
  { ring: string; iconBg: string; gradient: string; badge: string; btn: string }
> = {
  teal: {
    ring: 'hover:ring-[#4A7C75]/50',
    iconBg: 'bg-[#4A7C75]/15 text-[#4A7C75]',
    gradient: 'from-[#4A7C75]/10 to-transparent',
    badge: 'bg-[#EAF4F2] text-[#4A7C75]',
    btn: 'bg-[#4A7C75] hover:bg-[#3a6560]',
  },
  green: {
    ring: 'hover:ring-[#7ED99C]/50',
    iconBg: 'bg-[#7ED99C]/15 text-[#3d8f5c]',
    gradient: 'from-[#7ED99C]/10 to-transparent',
    badge: 'bg-[#7ED99C]/15 text-[#3d8f5c]',
    btn: 'bg-[#3d8f5c] hover:bg-[#2d6f45]',
  },
  purple: {
    ring: 'hover:ring-[#A56096]/50',
    iconBg: 'bg-[#A56096]/15 text-[#A56096]',
    gradient: 'from-[#A56096]/10 to-transparent',
    badge: 'bg-[#A56096]/10 text-[#A56096]',
    btn: 'bg-[#A56096] hover:bg-[#8a4f7f]',
  },
};

const guideIcons = [Video, Clock, Briefcase];

const TutorialsPage = () => {
  const whatsappLink =
    'https://wa.me/5513997701755?text=' +
    encodeURIComponent('Olá! Tenho dúvidas sobre os tutoriais da Constela Brasil.');

  return (
    <div className="min-h-screen bg-[#F3F6F1]">
      <AppHeader />

      <section className="px-4 pb-6 pt-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <span className="mb-4 inline-flex rounded-full bg-[#A56096]/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-[#A56096]">
            Material de apoio
          </span>
          <h1 className="mb-5 text-3xl font-bold leading-tight text-[#3A3157] sm:text-4xl lg:text-5xl">
            Tutoriais para{' '}
            <span className="text-[#4A7C75]">consteladores</span>
          </h1>
          <p className="mx-auto max-w-2xl text-base leading-relaxed text-[#496566] sm:text-lg">
            Assista ao vídeo tutorial, baixe os guias em PDF e aprenda a usar a plataforma passo a
            passo, da sala virtual à configuração da sua conta.
          </p>
        </div>
      </section>

      <section className="px-4 pb-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <div className="mb-6 text-center">
            <span className="mb-3 inline-flex rounded-full bg-[#4A7C75]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#4A7C75]">
              Vídeo tutorial
            </span>
            <h2 className="text-2xl font-bold text-[#3A3157] sm:text-3xl">
              {TUTORIAL_FEATURED_VIDEO.title}
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-[#496566] sm:text-base">
              {TUTORIAL_FEATURED_VIDEO.description}
            </p>
          </div>

          <div className="mkt-video">
            <iframe
              src={getTutorialVideoEmbedUrl(TUTORIAL_FEATURED_VIDEO.youtubeVideoId)}
              title={TUTORIAL_FEATURED_VIDEO.watchLabel}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              loading="lazy"
            />
          </div>

          <p className="mt-4 text-center text-sm text-[#496566]">
            Prefere abrir no YouTube?{' '}
            <a
              href={getTutorialVideoWatchUrl(TUTORIAL_FEATURED_VIDEO.youtubeVideoId)}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-[#A56096] underline decoration-[#A56096]/30 underline-offset-2 hover:text-[#8a4f7f]"
            >
              {TUTORIAL_FEATURED_VIDEO.watchLabel}
            </a>
          </p>
        </div>
      </section>

      <section className="px-4 pb-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-xl font-bold text-[#3A3157] sm:text-2xl">Guias em PDF</h2>
          <p className="mt-2 text-sm text-[#496566] sm:text-base">
            Baixe os materiais de apoio para consultar quando precisar.
          </p>
        </div>
      </section>

      <section className="px-4 pb-16 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-6xl gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {TUTORIAL_GUIDES.map((guide, index) => {
            const styles = accentStyles[guide.accent];
            const Icon = guideIcons[index] ?? BookOpen;
            const downloadUrl = getTutorialDownloadUrl(guide.fileName);

            return (
              <article
                key={guide.id}
                className={`group relative flex flex-col rounded-3xl bg-white p-7 shadow-lg ring-1 ring-black/5 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${styles.ring}`}
              >
                <div
                  className={`pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-br ${styles.gradient} opacity-0 transition-opacity duration-300 group-hover:opacity-100`}
                  aria-hidden
                />

                <div className="relative flex flex-1 flex-col">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <span
                      className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${styles.badge}`}
                    >
                      Guia {guide.number}
                    </span>
                    <span className="text-xs font-medium text-[#6b7280]">{guide.pages} págs.</span>
                  </div>

                  <div
                    className={`mb-5 flex h-14 w-14 items-center justify-center rounded-2xl ${styles.iconBg}`}
                  >
                    <Icon className="h-7 w-7" aria-hidden />
                  </div>

                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#A56096]">
                    {guide.subtitle}
                  </p>
                  <h2 className="mb-3 text-xl font-bold leading-snug text-[#3A3157]">
                    {guide.title}
                  </h2>
                  <p className="mb-6 flex-1 text-sm leading-relaxed text-[#496566]">
                    {guide.description}
                  </p>

                  <a
                    href={downloadUrl}
                    download
                    className={`inline-flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-3.5 text-sm font-semibold text-white shadow-md transition-colors ${styles.btn}`}
                  >
                    <Download className="h-4 w-4 shrink-0" aria-hidden />
                    Baixar PDF
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="px-4 pb-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl rounded-3xl border border-[#cdebd5] bg-white p-8 text-center shadow-sm sm:p-10">
          <h2 className="mb-2 text-xl font-bold text-[#3A3157] sm:text-2xl">Clube dos Consteladores</h2>
          <p className="mb-6 text-sm leading-relaxed text-[#496566] sm:text-base">
            Entre no grupo do WhatsApp para acompanhar avisos e trocar com outros consteladores.
          </p>
          <a
            href={CONSTELADOR_CLUB_WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-2xl bg-[#128C7E] px-6 py-3 text-sm font-semibold text-white shadow-md transition-colors hover:bg-[#0e7568]"
          >
            <MessageCircle className="h-4 w-4" aria-hidden />
            Entrar no grupo do WhatsApp
          </a>
        </div>
      </section>

      <section className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl rounded-3xl border border-[#cdebd5] bg-gradient-to-br from-[#e9f9ef] to-[#f3fbf6] p-8 text-center shadow-sm sm:p-10">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg">
            <MessageCircle className="h-7 w-7" aria-hidden />
          </div>
          <h2 className="mb-2 text-xl font-bold text-[#3A3157] sm:text-2xl">Precisa de ajuda?</h2>
          <p className="mb-6 text-sm leading-relaxed text-[#496566] sm:text-base">
            Nossa equipe está disponível pelo WhatsApp para tirar dúvidas sobre a plataforma e os
            tutoriais.
          </p>
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-2xl bg-[#25D366] px-6 py-3 text-sm font-semibold text-white shadow-md transition-colors hover:bg-[#1da851]"
          >
            <MessageCircle className="h-4 w-4" aria-hidden />
            (13) 99770-1755
          </a>
        </div>
      </section>

      <section className="border-t border-[#E5E9E2] bg-white px-4 py-10 sm:px-6">
        <div className="mx-auto max-w-4xl text-center">
          <p className="mb-4 text-[#496566]">Ainda não tem conta?</p>
          <a
            href={toAppUrl('/login')}
            className="inline-flex items-center gap-2 rounded-xl bg-[#3A5859] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#2A4849]"
          >
            Acessar a plataforma
          </a>
        </div>
      </section>
    </div>
  );
};

export default TutorialsPage;
